/** Resources */
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
/** Components */
import { CenterFrameLayout } from 'Layouts';
import { PlanPreview } from 'Features/Plans/PlanPreview';
import { PromoCodeForm } from 'Forms/PromoCodeForm/PromoCodeForm';
import { PaymentTotalDue } from 'Features/Payment/PaymentTotalDue/PaymentTotalDue';
import { SubmitError } from 'Components/Fields/SubmitError';
import { PaymentFinePrint } from 'Features/Payment/PaymentFinePrint';
import { PaymentMethodSelector } from 'Features/PaymentMethodSelector/PaymentMethodSelector';
import { CardPayment } from 'Features/PaymentMethodSelector/CardPayment';
import { PaypalPayment } from 'Features/PaymentMethodSelector/PaypalPayment';
import { SubmitPaymentButton } from 'Features/Payment/SubmitPaymentButton/SubmitPaymentButton';
import { Loader } from 'Components/Loader';
/** Helpers */
import { trackPaymentStarted } from 'Utils/PaymentMethods';
import { getSheerIdType, isSheerIdFreePlan } from 'Utils/SheerIdHelpers';
import { isFreeMonthPromoPlanActive, isMonthlyPromoPlanActive } from 'Utils/Plans';
import { PAYMENT_METHODS } from 'Features/PaymentMethodSelector/PaymentMethodSelector';
import { trackPaymentAction } from 'Utils/PaymentMethods';
import { usePayPal } from 'Hooks/usePayPal';
import { logger } from 'Utils/Logger';
import useVisualViewport from 'Hooks/useVisibleViewport';
import { fetchPaymentMethods, payment } from 'State/PaymentMethods/PaymentMethodActions';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import { updatePaymentAccount, setPaypalError } from 'State/Checkout/CheckoutActions';
import { FloatingBottomFooter } from 'Components/FloatingBottomFooter/FloatingBottomFooter';
import { selectPlan } from 'State/Plans/PlansActions';
import { getMemberVerify } from 'Api/SheerIdService';
import { DEEPLINK, MANAGE_PLAN_PURCHASE_MODE } from 'Utils/Constants';
import { useParams } from 'react-router-dom';
import { usePlans } from 'Hooks/usePlans';
import { setPurchaseMode } from 'State/PurchaseMode/PurchaseModeActions';
import './PaymentPage.css';

export const PaymentPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setLoading] = useState(true);
  const [selectedPaymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.CARD);
  const [isSheerIdFree, setIsSheerIdFree] = useState(false);
  const locale = useSelector((s) => s.appConfig.strings.payment);
  const { sheerIdTitle, sheerIdSubtitle } = locale;
  const routes = useSelector((s) => s.appConfig.routes);
  const purchaseMode = useSelector((s) => s.purchaseMode);
  const promoCode = useSelector((s) => s.promoCode);
  const appConfig = useSelector((s) => s.appConfig);
  const checkout = useSelector((s) => s.checkout);
  const paymentMethods = useSelector((s) => s.paymentMethods);
  const subscription = useSelector((s) => s.subscription);
  const plans = useSelector((s) => s.plans);
  const selectedPlanId = useSelector((s) => s.plans.selectedPlanId);
  const currentPaymentMethods = useSelector((s) => s.paymentMethods);
  const { preSelectedPlan = '' } = useParams();
  const { allPlans, isFetchingPlans, getPlansSuccess } = usePlans(preSelectedPlan);
  const selectedPlanStrings = allPlans.find((plan) => plan.planId === selectedPlanId);
  const [enablePaymentButton, setEnablePaymentButton] = useState(false);
  const [isUpdateCardMode, setIsUpdateCardMode] = useState(false);
  const [submitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSheerVerified, setIsSheerVerified] = useState(false);
  /* only true if saved credit card form and vermont checkbox is present/visible yet not checked */
  const [vtCheckMissing, setVtCheckMissing] = useState(false);
  const { isPlanSelected, selectedPlan } = plans;
  const { hasAccountUpdated } = checkout;
  const { isChurnedUser, hasSubscription, purchaseType, isSuccess, isChangePlanAvailable } = subscription;
  const [isPayPalEnabled] = usePayPal();
  const { hasApplePayPayment, hasCardPayment, hasPaypalPayment } = paymentMethods;
  const isMonthlyPromoActive = isPlanSelected && isMonthlyPromoPlanActive(selectedPlan);
  const isFreeMonthPromoActive = isPlanSelected && isFreeMonthPromoPlanActive(selectedPlan);
  const sheerIdType = getSheerIdType();
  const centerFrameSubtitle = isSheerIdFree && !isSheerVerified ? sheerIdSubtitle : '';
  const savedTypes = {
    [PAYMENT_METHODS.CARD]: hasCardPayment,
    [PAYMENT_METHODS.PAYPAL]: hasPaypalPayment,
    [PAYMENT_METHODS.APPLE]: hasApplePayPayment,
  };
  const hasSavedPayment = savedTypes[selectedPaymentMethod];
  const fromSheerVerification = location.state?.from === '/account/verification';
  const fromDeeplink = location.state?.from === DEEPLINK;
  // shows successful verification banner only if user comes from verification page or deeplink & is verified
  const shouldShowSuccessBanner = (fromSheerVerification || fromDeeplink) && isSheerVerified;

  // begin forwardRefs to submit payment forms
  const cardPaymentForm = useRef();
  const savedCardPaymentForm = useRef();
  const savedPaypalForm = useRef();
  // end forwardRefs
  const { isObstructed } = useVisualViewport();

  // Functionality for submit button in parent with forms in children components
  const submitPaymentForm = useCallback(
    (paymentMethod) => {
      if (paymentMethod === PAYMENT_METHODS.CARD && !hasCardPayment) {
        cardPaymentForm.current.submitForm();
      }
      if (paymentMethod === PAYMENT_METHODS.CARD && hasCardPayment) {
        //If Updating Card, the ref will switch back to cardPaymentForm
        if (savedCardPaymentForm.current === null) {
          cardPaymentForm.current.submitForm();
        } else {
          savedCardPaymentForm.current.submitForm();
        }
      }
      if (paymentMethod === PAYMENT_METHODS.PAYPAL && hasPaypalPayment) {
        savedPaypalForm.current.submitForm();
      }
    },
    [hasCardPayment, hasPaypalPayment, savedPaypalForm, savedCardPaymentForm, cardPaymentForm]
  );

  const dispatchSelectPlan = (planId) => {
    dispatch(selectPlan(planId));
  };

  const handlePayment = useCallback(
    async (type, data = {}, trackPaymentMethod) => {
      logger.started('handle payment', { type, data });

      return dispatch(payment(type, data, trackPaymentMethod))
        .then(() => {
          const confirmationURL = appConfig.routes.signupConfirmation;
          logger.completed('handle payment', {
            redirectUrl: confirmationURL,
          });
          setErrorMessage('');
          navigate(confirmationURL);
        })
        .catch((error) => {
          logger.error('handle payment', { type, data }, error);
          if (type === 'apple pay') {
            setErrorMessage(
              'Something went wrong, please check your Apple device and payment information and try again'
            );
          } else {
            setErrorMessage(error.message);
          }
          setIsSubmitting(false);
          return type === 'paypal' ? dispatch(setPaypalError(error.message)) : onSubmitReactFormError(error.message);
        });
    },
    [dispatch, appConfig, navigate]
  );

  const handleCardPayment = () => {
    handlePayment('card', { paymentmethodInfo: { label: 'Card' } }, PAYMENT_METHODS.CARD);
    setErrorMessage('');
  };

  const handleSavedPayment = (paymentmethodInfo, trackPaymentMethod) => {
    handlePayment('saved', { paymentmethodInfo }, trackPaymentMethod);
  };

  const getCenterFrameTitle = () => {
    let centerFrameTitle = 'Add Your Payment Method';
    if (isSheerIdFree && !isSheerVerified) {
      centerFrameTitle = sheerIdTitle;
    }
    if (isMonthlyPromoActive) {
      centerFrameTitle = 'Start Watching Now';
    }
    if (isFreeMonthPromoActive) {
      centerFrameTitle = '30-day free trial - Start watching now.';
    }
    if (isChurnedUser) {
      centerFrameTitle = 'Start Watching Now';
    }
    if (subscription.hasActiveSubscription) {
      centerFrameTitle = 'Submit Payment';
    }
    return centerFrameTitle;
  };

  const submitButtonText = useMemo(() => {
    let buttonText = 'Start Subscription';
    if (isMonthlyPromoActive) {
      buttonText = 'Join Now';
    }
    if (subscription.hasActiveSubscription || isSheerIdFree) buttonText = 'Submit';
    if (selectedPaymentMethod === 'paypal') buttonText = 'Continue with PayPal';
    if (selectedPaymentMethod === 'apple pay') buttonText = 'Buy with Pay';
    return buttonText;
  }, [isSheerIdFree, isMonthlyPromoActive, subscription, selectedPaymentMethod]);

  const paymentCustomClass = `${centerFrameSubtitle ? 'paymentFlowWithSub' : null} ${
    !isLoading && 'paymentPage'
  } signInLayout`;

  /** Begin useEffects */

  useEffect(() => {
    if (!isLoading) trackPaymentStarted();
  }, [isLoading]);

  useEffect(() => {
    if (!hasAccountUpdated) {
      dispatch(updatePaymentAccount());
    }
  }, [dispatch, hasAccountUpdated]);

  useEffect(() => {
    if (hasAccountUpdated && !paymentMethods.isSuccess && !paymentMethods.isFetching) {
      dispatch(fetchPaymentMethods());
    }
  }, [dispatch, paymentMethods, hasAccountUpdated]);

  useEffect(() => {
    if (hasAccountUpdated && paymentMethods.isSuccess) {
      setLoading(false);
    }
  }, [paymentMethods, hasAccountUpdated]);

  // set manage purchaseMode for existing active subscription users.
  useEffect(() => {
    if (isSuccess && hasSubscription && isChangePlanAvailable) {
      dispatch(setPurchaseMode(MANAGE_PLAN_PURCHASE_MODE));
    }
  }, [dispatch, isSuccess, hasSubscription, isChangePlanAvailable]);

  useEffect(() => {
    const getSheerVerification = async () => {
      try {
        const memberStatus = await getMemberVerify();
        const { currentStep } = memberStatus;
        setIsSheerVerified(currentStep === 'success');
      } catch (e) {
        setIsSheerVerified(false);
      }
      setIsSheerIdFree(isSheerIdFreePlan(!!selectedPlan && selectedPlan.packageID));
    };

    getSheerVerification();
  }, [selectedPlan]);

  /** End useEffects */

  if (preSelectedPlan && !getPlansSuccess && isFetchingPlans) {
    return <Loader />;
  }

  // check against null here instead of false because otherwise it will prematurely
  //    redirect while the app is still fetching plan info
  if (isPlanSelected == null) {
    return <Navigate replace to={appConfig.routes.signupPlans} />;
  }

  // if user that signed up from Nation app & has subscription tries to check out
  // we're redirecting them to current subscriber page
  if (hasSubscription && !!purchaseType && purchaseType !== 'web' && purchaseType !== 'coupon') {
    return <Navigate replace to={{ search: location.search, pathname: routes.signupCurrentSubscriber }} />;
  }

  return (
    <CenterFrameLayout
      title={getCenterFrameTitle()}
      subtitle={centerFrameSubtitle}
      showLoader={isLoading}
      customClass={paymentCustomClass}
      status={shouldShowSuccessBanner && 'Success! You’ve been verified.'}
      statusType={isSheerVerified && 'success'}
      modernLayout
    >
      <PlanPreview
        plan={selectedPlanStrings}
        isSheerIdFree={isSheerIdFree}
        subscription={subscription}
        promoMsg={promoCode.hasApplied && promoCode.promoDescription}
        allPlans={allPlans}
        selectPlan={dispatchSelectPlan}
      />
      <PromoCodeForm
        isSheerIdFree={isSheerIdFree}
        submitButtonText="Apply"
        addWrapper={true}
        canAutoApplyCoupon={true}
      />
      <PaymentMethodSelector
        selectedPaymentMethod={selectedPaymentMethod}
        setPaymentMethod={setPaymentMethod}
        setErrorMessage={setErrorMessage}
      />
      {selectedPaymentMethod === PAYMENT_METHODS.CARD && (
        <CardPayment
          handleCardPayment={(data) => handleCardPayment(data, PAYMENT_METHODS.CARD)}
          handleSavedPayment={(data) => handleSavedPayment(data, PAYMENT_METHODS.CARD)}
          submitButtonText={submitButtonText}
          trackPaymentAction={trackPaymentAction}
          cardPaymentForm={cardPaymentForm}
          setEnablePaymentButton={setEnablePaymentButton}
          savedCardPaymentForm={savedCardPaymentForm}
          setIsSubmitting={setIsSubmitting}
          hasSavedPayment={hasSavedPayment}
          setIsUpdateCardMode={setIsUpdateCardMode}
          setVtCheckMissing={setVtCheckMissing}
          setErrorMessage={setErrorMessage}
        />
      )}
      {isPayPalEnabled &&
        selectedPaymentMethod === PAYMENT_METHODS.PAYPAL &&
        currentPaymentMethods.hasPaypalPayment && (
          <PaypalPayment
            handleSavedPayment={(data) => handleSavedPayment(data, PAYMENT_METHODS.PAYPAL)}
            submitButtonText={submitButtonText}
            savedPaypalForm={savedPaypalForm}
          />
        )}
      {!currentPaymentMethods.hasPaypalPayment && !selectedPaymentMethod === PAYMENT_METHODS.PAYPAL && (
        <hr className="sectionLine bottom" />
      )}
      <PaymentTotalDue />
      <SubmitError error={errorMessage} trackError={false} />
      <SubmitPaymentButton
        paymentType={selectedPaymentMethod}
        hasSavedPayment={hasSavedPayment}
        handlePayment={handlePayment}
        submitPaymentForm={submitPaymentForm}
        submitButtonText={submitButtonText}
        submitting={submitting}
        setIsSubmitting={setIsSubmitting}
        isObstructed={isObstructed}
        isDisabled={!enablePaymentButton && (!hasSavedPayment || isUpdateCardMode || vtCheckMissing)}
        trackPaymentAction={trackPaymentAction}
      />
      <PaymentFinePrint
        purchaseMode={purchaseMode}
        ctaText={submitButtonText}
        sheerIdType={sheerIdType}
        selectedPlanId={selectedPlanId}
      />
      <FloatingBottomFooter
        component={
          <SubmitPaymentButton
            paymentType={selectedPaymentMethod}
            hasSavedPayment={hasSavedPayment}
            handlePayment={handlePayment}
            submitPaymentForm={submitPaymentForm}
            submitButtonText={submitButtonText}
            submitting={submitting}
            setIsSubmitting={setIsSubmitting}
            isObstructed={isObstructed}
            isDisabled={!enablePaymentButton && (!hasSavedPayment || isUpdateCardMode || vtCheckMissing)}
            trackPaymentAction={trackPaymentAction}
            className={'floatingSubmitMobileOnly'}
          />
        }
        mobileOnly={true}
      />
    </CenterFrameLayout>
  );
};
